/**
 * CardSchedule — Card de agendamento para a visão desktop/lista.
 *
 * Layout: barra lateral colorida de status · bloco de data · conteúdo · ações.
 * Toda a lógica de negócio (teleconsulta, pagamento, seleção) está preservada.
 */
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AttachMoney,
  Chat,
  Home,
  LocalHospital,
  MedicalServices,
  PersonOutline,
  VideoCameraFront,
} from "@mui/icons-material";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { useEffect, useState } from "react";

import { useFormatter } from "../../utils/useFormatter";
import { useSessionContext } from "../../contexts/SessionContext";
import { useDatetime } from "../../utils/useDatetime";
import VideoConferencia from "../videoConferencia/VideoConferencia";
import { useClinicalRegisterContext } from "../../contexts/ClinicalRegisterContext";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";

interface IProps {
  data: any;
  selected: number;
  index: number;
  onClick: any;
  locationTeleconsulting?: boolean;
  setOpenAppointmentDetails?: any;
  locationAttendanceChannel?: string;
  hideTeleconsultBecausePaymentPending?: boolean;
}

// ─── Paleta de status ─────────────────────────────────────────────────────────

type StatusKey = "booked" | "arrived" | "pending" | "proposed" | "cancelled";

const STATUS_STYLE: Record<StatusKey, { accent: string; bg: string; chipColor: "success" | "default" | "warning" | "error" | "primary" }> = {
  booked:    { accent: "#007C6D", bg: "#F0FAF9", chipColor: "success"  },
  arrived:   { accent: "#007C6D", bg: "#F0FAF9", chipColor: "success"  },
  // Pendente: mantém cor warning (amber) igual ao mobile
  pending:   { accent: "#D97706", bg: "#FFFBEB", chipColor: "warning"  },
  proposed:  { accent: "#D97706", bg: "#FFFBEB", chipColor: "warning"  },
  cancelled: { accent: "#DC2626", bg: "#FEF2F2", chipColor: "error"    },
};

// ─── Ícones por tipo de atendimento ───────────────────────────────────────────

type AttendanceChannel = "chat" | "teleatendimento" | "presencial" | "domicilio";

const ATTENDANCE_CONFIG: Record<AttendanceChannel, { Icon: React.ElementType; label: string }> = {
  chat:            { Icon: Chat,             label: "Chat"                   },
  teleatendimento: { Icon: VideoCameraFront, label: "Teleconsulta por vídeo" },
  presencial:      { Icon: LocalHospital,    label: "Consulta presencial"    },
  domicilio:       { Icon: Home,             label: "Atendimento domiciliar" },
};

const STATUS_LABEL: Record<string, string> = {
  booked: "Confirmado", arrived: "Confirmado",
  pending: "Pendente",  proposed: "Proposta",
  cancelled: "Cancelado",
};

// ─── Componente ───────────────────────────────────────────────────────────────

export function CardSchedule({
  data,
  selected,
  index,
  onClick,
  locationTeleconsulting,
  setOpenAppointmentDetails,
  locationAttendanceChannel,
  hideTeleconsultBecausePaymentPending,
}: IProps) {
  const { access, user } = useSessionContext();
  const theme = useTheme();
  const { inProgress } = useClinicalRegisterContext();
  const { formatDateTimeToHour } = useFormatter;
  const { formatDate } = useDatetime;
  const { formatNameDisplay, formatAge } = useFormatter;
  const [hasPaymentLink, setHasPaymentLink] = useState(false);

  // Verifica link de pagamento para consultas pendentes (mesma lógica do mobile)
  useEffect(() => {
    const checkPaymentLink = async () => {
      try {
        if (data?.slot?.split("/")?.[1]) {
          await marketplaceService.getPaymentLinkBySlotId(data?.slot?.split("/")?.[1]);
          setHasPaymentLink(true);
        }
      } catch {
        setHasPaymentLink(false);
      }
    };
    if (data?.status === "pending" && data?.slot) checkPaymentLink();
  }, [data?.slot, data?.status]);

  const showPayButton =
    access?.type === "patient" &&
    data?.status === "pending" &&
    hasPaymentLink;
  const showVideoButton =
    locationTeleconsulting &&
    !hideTeleconsultBecausePaymentPending &&
    data?.status === "booked";

  const isLate      = dayjs().isAfter(dayjs(data.startTime).add(10, "minute"));
  const isSelected  = selected === index;
  const isInProgress = inProgress === data?.id;

  const status: StatusKey = data.status as StatusKey;
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.booked;

  // Estados especiais sobrescrevem a cor de destaque
  const accentColor  = isInProgress ? "#8C9099" : isLate && access?.type === "professional" ? "#F59E0B" : style.accent;
  const bgColor      = isInProgress ? "#F1F3F6" : isLate && access?.type === "professional" ? "#FFFBEB" : style.bg;
  const statusLabel  = isInProgress ? "Iniciado" : isLate && access?.type === "professional" ? "Atrasado" : STATUS_LABEL[status] ?? status;

  // Data formatada em partes para o bloco de calendário
  const dt         = dayjs(data.startTime).locale("pt-br");
  const dayName    = dt.format("ddd").replace(".", "").toUpperCase();   // "TER"
  const dayNumber  = dt.format("DD");                                    // "15"
  const monthName  = dt.format("MMM").replace(".", "").toUpperCase();   // "ABR"
  const time       = formatDateTimeToHour(data.startTime) || "--:--";

  const appointmentData = {
    id: data.id,
    patientName: data.patientName,
    practitionerName: data.practitioner,
    startTime: data.startTime,
    status: data.status,
  };

  // Tipo de atendimento para ícone + tooltip
  const attendanceKey: AttendanceChannel =
    (locationAttendanceChannel as AttendanceChannel) ??
    (locationTeleconsulting ? "teleatendimento" : "presencial");
  const attendance = ATTENDANCE_CONFIG[attendanceKey];

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      sx={{
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        my: 1,
        minHeight: 120,
        borderRadius: "10px",
        // Selecionado: borda 2px sólida e fundo levemente tintado
        border: isSelected ? "2px solid" : "1px solid",
        borderColor: isSelected ? accentColor : alpha(accentColor, 0.3),
        bgcolor: isSelected ? alpha(accentColor, 0.05) : bgColor,
        // overflow:visible para não cortar conteúdo; borderRadius visível pelo border
        overflow: "visible",
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
        boxShadow: isSelected
          ? `0 0 0 3px ${alpha(accentColor, 0.25)}, 0 4px 14px ${alpha(accentColor, 0.2)}`
          : "0 1px 3px rgba(0,0,0,0.06)",
        "&:hover": {
          boxShadow: `0 2px 10px ${alpha(accentColor, 0.2)}`,
          borderColor: accentColor,
        },
      }}
    >
      {/* Barra lateral — cantos arredondados no lado esquerdo do card */}
      <Box sx={{
        width: isSelected ? 6 : 4,
        flexShrink: 0,
        alignSelf: "stretch",
        bgcolor: accentColor,
        borderRadius: "8px 0 0 8px",
        transition: "width 0.15s ease",
      }} />

      {/* ── Coluna de data ────────────────────────────────────────────────────── */}
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 72,
          flexShrink: 0,
          alignSelf: "stretch",
          borderRight: "1px solid",
          borderColor: alpha(accentColor, 0.18),
          py: 2,
          gap: "2px",
          bgcolor: isSelected ? alpha(accentColor, 0.13) : alpha(accentColor, 0.06),
          transition: "background-color 0.15s ease",
        }}
      >
        {/* Dia da semana */}
        <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, color: accentColor, letterSpacing: "0.08em", lineHeight: 1 }}>
          {dayName}
        </Typography>
        {/* Número do dia — maior destaque */}
        <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: "text.primary", lineHeight: 1.1 }}>
          {dayNumber}
        </Typography>
        {/* Mês */}
        <Typography sx={{ fontSize: "0.58rem", color: "text.secondary", letterSpacing: "0.06em", lineHeight: 1 }}>
          {monthName}
        </Typography>
        {/* Divisor */}
        <Box sx={{ width: "50%", height: "1px", bgcolor: alpha(accentColor, 0.3), my: 0.5 }} />
        {/* Horário — destaque maior conforme solicitado */}
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: accentColor, lineHeight: 1 }}>
          {time}
        </Typography>
      </Stack>

      {/* ── Conteúdo principal ────────────────────────────────────────────────── */}
      <Stack
        justifyContent="center"
        spacing={0.6}
        sx={{ flex: 1, minWidth: 0, py: 2, pl: 2, pr: 1 }}
      >
        {access?.type === "patient" ? (
          <>
            {/* 1. Nome do médico */}
            <Typography variant="subtitle2" fontWeight={700} noWrap color="text.primary" sx={{ lineHeight: 1.3 }}>
              {data.practitioner
                ? /^dr\(a\)\./i.test(data.practitioner.trim())
                  ? data.practitioner                                                    // já tem Dr(a). → mantém
                  : `Dr(a). ${data.practitioner.replace(/^dr\.?\s*/i, "").trim()}`      // remove "Dr." e adiciona Dr(a).
                : "Profissional não informado"}
            </Typography>

            {/* 2. Paciente — alinhado à esquerda igual às demais linhas */}
            {data.patientName && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PersonOutline sx={{ fontSize: 12, color: "text.disabled", flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: "0.72rem", fontWeight: 500, color: "text.secondary", lineHeight: 1.3 }}>
                  {formatNameDisplay(data.patientName)}
                </Typography>
              </Stack>
            )}

            {/* 3. Especialidade */}
            {data.healthcare && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <MedicalServices sx={{ fontSize: 12, color: "text.disabled", flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: "0.72rem", fontWeight: 500, color: "text.secondary", lineHeight: 1.3 }}>
                  {data.healthcare}
                </Typography>
              </Stack>
            )}

            {/* 4. Tipo de consulta */}
            <Tooltip title={attendance.label} placement="bottom-start">
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: "fit-content" }}>
                <attendance.Icon sx={{ fontSize: 12, color: accentColor, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: accentColor, lineHeight: 1.3 }}>
                  {attendance.label}
                </Typography>
              </Stack>
            </Tooltip>
          </>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={700} noWrap color="text.primary">
              {formatNameDisplay(data.patientName)}
            </Typography>
            {dayjs(data.patientBirthday).isValid() && (
              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }} noWrap>
                {formatDate(data.patientBirthday)} · {formatAge(data.patientBirthday)} anos
              </Typography>
            )}
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              {data.patientSex}
            </Typography>
          </>
        )}
      </Stack>

      {/* ── Coluna de ações ───────────────────────────────────────────────────── */}
      {access?.type === "patient" && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1.25}
          sx={{ flexShrink: 0, alignSelf: "stretch", py: 2, pr: 1.5, pl: 0.5 }}
        >
          {/* Chip de status */}
          <Chip
            label={statusLabel}
            size="small"
            color={style.chipColor}
            variant={isSelected ? "filled" : "outlined"}
            sx={{ height: 22, fontSize: "0.64rem", fontWeight: 700 }}
          />

          {/* Link de detalhes */}
          <Tooltip title="Ver detalhes da consulta">
            <Typography
              sx={{ fontSize: "0.72rem", fontWeight: 700, color: "primary.main", cursor: "pointer", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}
              onClick={(e) => { e.stopPropagation(); setOpenAppointmentDetails?.(true); }}
            >
              Detalhes
            </Typography>
          </Tooltip>

          {/* Botão pagar — destaque visual para consultas pendentes */}
          {showPayButton && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={
                <AttachMoney sx={{ fontSize: 15, color: "inherit !important" }} />
              }
              onClick={async (e) => {
                e.stopPropagation();
                const link = await marketplaceService.getPaymentLinkBySlotId(
                  data?.slot.split("/")?.[1],
                );
                window.open(link, "_blank");
              }}
              sx={{
                bgcolor: "warning.main",
                color: "neutral0.main",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.02em",
                borderRadius: "10px",
                py: 0.55,
                px: 1.1,
                minWidth: 0,
                whiteSpace: "nowrap",
                boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.35)}`,
                transition: "transform 0.12s ease, box-shadow 0.12s ease",
                "&:hover": {
                  bgcolor: "warning700.main",
                  color: "neutral0.main",
                  boxShadow: `0 3px 12px ${alpha(theme.palette.warning.main, 0.45)}`,
                },
                "&:active": { transform: "scale(0.96)" },
                "& .MuiButton-startIcon": { mr: 0.35 },
              }}
            >
              Pagar
            </Button>
          )}

          {/* Botão de teleconsulta */}
          {showVideoButton && (
            <Box onClick={(e) => e.stopPropagation()}>
              <VideoConferencia
                roomUrl={data?.virtualService}
                cardSchedule
                appointmentData={appointmentData}
                attendanceType={locationAttendanceChannel}
              />
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
